'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Volume2, VolumeX } from 'lucide-react';
import { apiHooks, type TrendPreset } from '@/lib/api';
import { AuthTrigger } from './AuthTrigger';

interface TrendDetailProps {
  id: string;
}

export function TrendDetail({ id }: TrendDetailProps) {
  const { data: trend, isLoading, isError } = useQuery({
    queryKey: ['trend', id],
    queryFn: () => apiHooks.getTrendById(id),
    staleTime: 5 * 60 * 1000,
  });
  const { data: allTrends } = useQuery({
    queryKey: ['trends', 'all'],
    queryFn: () => apiHooks.getTrends(),
    staleTime: 5 * 60 * 1000,
  });
  const router = useRouter();

  const siblings = useMemo(() => {
    if (!allTrends?.trends || !trend) return { prevId: null, nextId: null };
    const list = allTrends.trends;
    const index = list.findIndex((t) => t.id === trend.id);
    if (index === -1) return { prevId: null, nextId: null };
    const prevId = list[(index - 1 + list.length) % list.length]?.id ?? null;
    const nextId = list[(index + 1) % list.length]?.id ?? null;
    return { prevId, nextId };
  }, [allTrends, trend]);

  if (isLoading) {
    return (
      <section className="mx-auto max-w-[1440px] px-5 py-20 md:px-[180px]">
        <div className="h-[60vh] animate-pulse rounded-3xl bg-white/5" />
      </section>
    );
  }

  if (isError || !trend) {
    return (
      <section className="mx-auto max-w-[1440px] px-5 py-20 text-center md:px-[180px]">
        <h1 className="font-display text-3xl font-bold">Тренд не найден</h1>
        <p className="mt-4 text-white/60">Попробуйте вернуться к списку трендов.</p>
        <Link
          href="/trends"
          className="mt-8 inline-flex h-12 items-center justify-center rounded-[20px] bg-[#ef6444] px-6 text-sm font-semibold text-white transition-colors hover:bg-[#d3573b]"
        >
          К трендам
        </Link>
      </section>
    );
  }

  const examples = trend.examples.slice(0, 3);
  const hasRecommended = (trend.recommendedTrends?.length ?? 0) > 0;
  const related: RelatedItem[] = hasRecommended
    ? trend.recommendedTrends!.slice(0, 6)
    : (allTrends?.trends ?? [])
        .filter((t) => t.id !== trend.id && (!trend.category || t.category === trend.category))
        .slice(0, 6)
        .map((t) => ({
          id: t.id,
          name: t.name,
          examples: t.examples.map((e) => ({
            id: e.id,
            imageUrl: e.imageUrl,
            thumbnailUrl: e.thumbnailUrl,
            isVideo: e.isVideo,
          })),
        }));

  return (
    <section className="relative overflow-hidden">
      <div className="mx-auto max-w-[1440px] px-5 pb-20 pt-8 md:px-[100px] md:pb-32 md:pt-[50px]">
        <HeroSplit
          trend={trend}
          examples={examples}
          onPrev={siblings.prevId ? () => router.push(`/trends/${siblings.prevId}`) : undefined}
          onNext={siblings.nextId ? () => router.push(`/trends/${siblings.nextId}`) : undefined}
        />
        <RequirementsBlock description={trend.description} />
        <HowToSteps trendName={trend.name} />
        {related.length > 0 ? <RelatedTrends items={related} /> : null}
      </div>
    </section>
  );
}

type RelatedItem = NonNullable<TrendPreset['recommendedTrends']>[number];

function HeroSplit({
  trend,
  examples,
  onPrev,
  onNext,
}: {
  trend: TrendPreset;
  examples: TrendPreset['examples'];
  onPrev?: () => void;
  onNext?: () => void;
}) {
  return (
    <div className="grid gap-10 md:grid-cols-[507px_1fr] md:items-center md:gap-[50px]">
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-[31px]">
          <h1 className="font-display text-[32px] font-bold leading-[1.275] text-white md:text-[40px]">
            {trend.name}
          </h1>
          <div className="h-px w-full bg-[#25272a]" />
        </div>

        {trend.description ? (
          <p
            className="text-base leading-[1.6] text-white md:text-lg"
            style={{ letterSpacing: '-0.4px' }}
          >
            {trend.description}
          </p>
        ) : null}

        <div className="flex flex-col gap-5 md:max-w-[325px]">
          <AuthTrigger className="inline-flex h-14 items-center justify-center rounded-[20px] bg-[#ef6444] px-6 text-lg font-semibold text-white transition-colors hover:bg-[#d3573b] md:w-full md:text-[18px]">
            Повторить этот тренд
          </AuthTrigger>

          <div className="flex items-center justify-start gap-2">
            <ArrowButton direction="left" onClick={onPrev} />
            <ArrowButton direction="right" onClick={onNext} />
          </div>
        </div>
      </div>

      <div className="relative min-h-[500px] md:min-h-[660px]">
        {examples.length > 0 ? (
          <HeroStack examples={examples} name={trend.name} />
        ) : (
          <div className="h-[500px] rounded-[40px_8px_40px_8px] bg-white/5 md:h-[660px]" />
        )}
      </div>
    </div>
  );
}

function ArrowButton({
  direction,
  onClick,
}: {
  direction: 'left' | 'right';
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={direction === 'left' ? 'Предыдущий тренд' : 'Следующий тренд'}
      onClick={onClick}
      disabled={!onClick}
      className="flex h-[61px] w-[61px] items-center justify-center transition-opacity hover:opacity-70 disabled:cursor-not-allowed disabled:opacity-40"
    >
      <svg
        width="47"
        height="7"
        viewBox="0 0 47 7"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ transform: direction === 'right' ? 'scaleX(-1)' : undefined }}
      >
        <path
          d="M5.4386 5.07825L3.14561 3.51909L46.7539 3.51787V2.29787L3.41889 2.29909L5.5789 1.05774L4.97134 0L0.00044632 2.85602L4.75235 6.08719L5.4386 5.07825Z"
          fill="white"
        />
      </svg>
    </button>
  );
}

function HeroStack({
  examples,
  name,
}: {
  examples: TrendPreset['examples'];
  name: string;
}) {
  const single = examples.length === 1;

  return (
    <>
      <div className="flex justify-center md:hidden">
        {examples.slice(0, 1).map((example) => (
          <HeroCard key={example.id} example={example} name={name} size="mobile" />
        ))}
      </div>

      {single ? (
        <div className="hidden h-[660px] w-full items-center justify-center md:flex">
          <div className="aspect-[333/499] h-[80%] max-h-[528px]">
            <HeroCard
              example={examples[0]!}
              name={name}
              size="desktop"
            />
          </div>
        </div>
      ) : (
        <div className="hidden items-start gap-4 md:flex md:w-[150%] md:-mr-[50%]">
          {examples.map((example, index) => {
            const offsets = [30, 80, 0];
            const marginTop = offsets[index] ?? 0;
            return (
              <div
                key={example.id}
                className="aspect-[333/499] flex-1 basis-0"
                style={{ marginTop }}
              >
                <HeroCard example={example} name={name} size="desktop" />
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function HeroCard({
  example,
  name,
  size,
}: {
  example: TrendPreset['examples'][number];
  name: string;
  size: 'mobile' | 'desktop';
}) {
  const isMobile = size === 'mobile';
  const videoRef = useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = muted;
    if (!muted) video.play().catch(() => {});
  }, [muted]);

  return (
    <div
      className={`relative overflow-hidden bg-[#1a1a1d] ${isMobile ? 'aspect-[333/499] w-full max-w-[420px]' : 'h-full w-full'}`}
      style={{
        borderRadius: '41.58px 8.31px 41.58px 8.31px',
        boxShadow:
          '0 30px 60px -20px rgba(0, 0, 0, 0.65), 0 15px 30px -15px rgba(0, 0, 0, 0.55)',
      }}
    >
      {example.isVideo ? (
        <>
          <video
            ref={videoRef}
            src={example.imageUrl}
            poster={example.thumbnailUrl ?? undefined}
            autoPlay
            muted
            loop
            playsInline
            className="h-full w-full object-cover"
          />
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setMuted((m) => !m);
            }}
            aria-label={muted ? 'Включить звук' : 'Выключить звук'}
            aria-pressed={!muted}
            className="absolute bottom-4 right-4 inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-sm transition-colors hover:bg-black/80"
          >
            {muted ? <VolumeX size={18} strokeWidth={2} /> : <Volume2 size={18} strokeWidth={2} />}
          </button>
        </>
      ) : (
        /* eslint-disable-next-line @next/next/no-img-element */
        <img
          src={example.imageUrl}
          alt={name}
          loading="lazy"
          className="h-full w-full object-cover"
        />
      )}
    </div>
  );
}


function RequirementsBlock({ description }: { description: string | null }) {
  if (!description) return null;
  return (
    <div className="mt-4 flex flex-col gap-4 md:mt-2 md:pl-[120px]">
      <h2 className="font-display text-2xl font-bold leading-tight text-white">
        Что нужно:
      </h2>
      <p
        className="whitespace-pre-wrap text-base leading-[1.6] text-white md:text-lg"
        style={{ letterSpacing: '-0.4px' }}
      >
        {description}
      </p>
    </div>
  );
}

function HowToSteps({ trendName }: { trendName: string }) {
  const steps = [
    {
      number: '01',
      title: 'Выберите стиль',
      text: `Выберите стиль «${trendName}» на странице ИИ-сервисов`,
    },
    {
      number: '02',
      title: 'Загрузите фото',
      text: 'Выберите фото из вашей галереи и нажмите «Создать фото»',
    },
    {
      number: '03',
      title: 'Получите результат',
      text: 'Скачайте фото через несколько секунд',
    },
  ];

  return (
    <div className="mt-8 flex flex-col gap-6 md:mt-10 md:pl-[120px]">
      <h2 className="font-display text-2xl font-bold leading-tight text-white md:text-[24px]">
        Как создать тренд «{trendName}»?
      </h2>
      <div className="flex flex-col gap-6">
        {steps.map((step) => (
          <div key={step.number} className="flex items-start gap-6 md:gap-9">
            <div
              className="shrink-0 text-[48px] font-bold leading-none text-[#d1d1d6] md:text-[64px]"
              style={{
                fontFamily: 'Times New Roman, Times, serif',
                letterSpacing: '0.01em',
              }}
            >
              {step.number}
            </div>
            <div className="flex flex-1 flex-col gap-2 pt-1 md:pt-3">
              <div className="font-display text-lg font-bold text-white">
                {step.title}
              </div>
              <div
                className="text-base leading-[1.6] text-white md:text-lg"
                style={{ letterSpacing: '-0.4px' }}
              >
                {step.text}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RelatedTrends({ items }: { items: RelatedItem[] }) {
  const scrollerRef = useRef<HTMLDivElement>(null);

  const scrollBy = (direction: 'left' | 'right') => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.9;
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  return (
    <div className="mt-20 flex flex-col gap-6 md:mt-28">
      <div className="flex items-center justify-between gap-4">
        <h2 className="font-display text-3xl font-bold leading-[1.275] text-white md:text-[40px]">
          Похожие ИИ-тренды
        </h2>
        <div className="hidden items-center gap-2 md:flex">
          <ArrowButton direction="left" onClick={() => scrollBy('left')} />
          <ArrowButton direction="right" onClick={() => scrollBy('right')} />
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="flex gap-4 overflow-x-auto pb-2 md:gap-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {items.map((item) => {
          const imageUrl = item.examples[0]?.imageUrl;
          const isVideo = item.examples[0]?.isVideo;
          const thumb = item.examples[0]?.thumbnailUrl;
          return (
            <Link
              key={item.id}
              href={`/trends/${item.id}`}
              className="group relative block aspect-[252/378] w-[calc((100%-1rem)/2)] shrink-0 overflow-hidden bg-[#1a1a1d] sm:w-[calc((100%-2rem)/3)] md:w-[calc((100%-4.5rem)/4)]"
              style={{ borderRadius: '31.5px 6.3px 6.3px 31.5px' }}
            >
              {imageUrl ? (
                isVideo ? (
                  <video
                    src={imageUrl}
                    poster={thumb ?? undefined}
                    muted
                    loop
                    playsInline
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  /* eslint-disable-next-line @next/next/no-img-element */
                  <img
                    src={imageUrl}
                    alt={item.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                )
              ) : null}
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[48%] bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-4 md:p-5">
                <div className="font-display text-lg leading-tight text-white md:text-[25px]">
                  {item.name}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
